import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { asArray, asRecord, normalizeJsonValue, runDomainRouterQuery } from './graphql';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type AdmissionsDashboardData = {
  items: Record<string, unknown>[];
  totalCount: number;
  nextToken: string | null;
};

type AdmissionsDetailData = {
  application: Record<string, unknown> | null;
  documents: Record<string, unknown>[];
  timeline: Record<string, unknown>[];
};

type AdmissionsDetailEntry = {
  status: AsyncStatus;
  error: string | null;
  data: AdmissionsDetailData | null;
};

type AdmissionsLiveState = {
  dashboard: {
    status: AsyncStatus;
    error: string | null;
    data: AdmissionsDashboardData;
  };
  detailByRouteId: Record<string, AdmissionsDetailEntry>;
};

const initialState: AdmissionsLiveState = {
  dashboard: {
    status: 'idle',
    error: null,
    data: {
      items: [],
      totalCount: 0,
      nextToken: null,
    },
  },
  detailByRouteId: {},
};

const createEmptyDetail = (): AdmissionsDetailEntry => ({
  status: 'idle',
  error: null,
  data: null,
});

const findApplicationMatch = (items: Record<string, unknown>[], routeId: string): Record<string, unknown> | null =>
  items.find((item) => {
    const id = String(item.id ?? '').trim();
    const externalId = String(item.externalId ?? '').trim();
    const applicantId = String(item.applicantId ?? '').trim();
    return routeId === id || routeId === externalId || routeId === applicantId;
  }) ?? null;

export const fetchAdmissionsDashboard = createAsyncThunk<AdmissionsDashboardData, number | undefined>(
  'admissionsLive/fetchDashboard',
  async (limit = 50): Promise<AdmissionsDashboardData> => {
    const payload = asRecord(await runDomainRouterQuery('listApplications', { limit }));
    return {
      items: asArray<Record<string, unknown>>(payload.items),
      totalCount: Number(payload.totalCount ?? 0),
      nextToken: payload.nextToken ? String(payload.nextToken) : null,
    };
  }
);

export const fetchAdmissionsDetail = createAsyncThunk(
  'admissionsLive/fetchDetail',
  async (routeId: string): Promise<{ routeId: string; detail: AdmissionsDetailData }> => {
    const normalizedRouteId = String(routeId ?? '').trim();

    let application: Record<string, unknown> | null = null;
    try {
      application = asRecord(await runDomainRouterQuery('getApplication', { id: normalizedRouteId }));
    } catch {
      const listData = asRecord(await runDomainRouterQuery('listApplications', { limit: 200 }));
      const listItems = asArray<Record<string, unknown>>(listData.items);
      application = findApplicationMatch(listItems, normalizedRouteId);
    }

    const applicationId = String(application?.id ?? normalizedRouteId).trim();

    const documentsData = asRecord(
      await runDomainRouterQuery('getApplicationDocuments', { applicationId, limit: 200 })
    );
    const documents = asArray<Record<string, unknown>>(documentsData.items);

    const timelineData = asRecord(await runDomainRouterQuery('listTimelineEvents', {
      filter: JSON.stringify(normalizeJsonValue<Record<string, unknown>>({ applicationId }, {})),
      limit: 200,
    }));
    const timeline = asArray<Record<string, unknown>>(timelineData.items);

    return {
      routeId: normalizedRouteId,
      detail: {
        application,
        documents,
        timeline,
      },
    };
  }
);

const admissionsLiveSlice = createSlice({
  name: 'admissionsLive',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdmissionsDashboard.pending, (state) => {
        state.dashboard.status = 'loading';
        state.dashboard.error = null;
      })
      .addCase(fetchAdmissionsDashboard.fulfilled, (state, action: PayloadAction<AdmissionsDashboardData>) => {
        state.dashboard.status = 'succeeded';
        state.dashboard.data = action.payload;
      })
      .addCase(fetchAdmissionsDashboard.rejected, (state, action) => {
        state.dashboard.status = 'failed';
        state.dashboard.error = action.error.message ?? 'Failed to load admissions dashboard';
      })
      .addCase(fetchAdmissionsDetail.pending, (state, action) => {
        const routeId = action.meta.arg;
        const entry = state.detailByRouteId[routeId] ?? createEmptyDetail();
        entry.status = 'loading';
        entry.error = null;
        state.detailByRouteId[routeId] = entry;
      })
      .addCase(fetchAdmissionsDetail.fulfilled, (state, action) => {
        const { routeId, detail } = action.payload;
        state.detailByRouteId[routeId] = {
          status: 'succeeded',
          error: null,
          data: detail,
        };
      })
      .addCase(fetchAdmissionsDetail.rejected, (state, action) => {
        const routeId = action.meta.arg;
        const entry = state.detailByRouteId[routeId] ?? createEmptyDetail();
        entry.status = 'failed';
        entry.error = action.error.message ?? 'Failed to load admissions detail';
        state.detailByRouteId[routeId] = entry;
      });
  },
});

export const admissionsLiveReducer = admissionsLiveSlice.reducer;
